import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import IpdFormField from '../ipd/utils/IpdFormField';
import { searchOtCatalogServices } from './otChargeApi';
import { inputSx } from '../inventory/theme';

const SEARCH_DEBOUNCE_MS = 300;

function optionSubline(item) {
  const bits = [];
  if (item.service_code) bits.push(item.service_code);
  if (item.fee != null && item.fee !== '' && Number.isFinite(Number(item.fee))) {
    bits.push(`₹${Number(item.fee).toLocaleString('en-IN')}${item.unit === 'PER_30_MIN' ? ' / 30 min' : ''}`);
  }
  return bits.join(' · ');
}

export default function ChargeServiceAutocomplete({
  label,
  placeholder = 'Type to search…',
  value,
  onChange,
  classificationName,
  required,
}) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const seqRef = useRef(0);

  const runSearch = useCallback((query) => {
    const seq = ++seqRef.current;
    setLoading(true);
    searchOtCatalogServices({ classificationName, name: query, limit: 50 })
      .then((mapped) => {
        if (seq !== seqRef.current) return;
        setOptions(mapped);
      })
      .catch(() => {
        if (seq !== seqRef.current) return;
        setOptions([]);
      })
      .finally(() => {
        if (seq === seqRef.current) setLoading(false);
      });
  }, [classificationName]);

  useEffect(() => {
    if (!open) return undefined;
    const handle = window.setTimeout(() => runSearch(inputValue), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [inputValue, open, runSearch]);

  const displayOptions = useMemo(() => {
    if (value && !options.some((item) => item.id === value.id)) return [value, ...options];
    return options;
  }, [options, value]);

  const noOptionsText = loading ? 'Searching…' : 'No matches';

  return (
    <IpdFormField label={label} required={required}>
      <Autocomplete
        size="small"
        fullWidth
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={displayOptions}
        loading={loading}
        value={value || null}
        inputValue={inputValue}
        onInputChange={(_, next, reason) => {
          if (reason === 'input' || reason === 'clear') {
            setInputValue(next);
            if (value && next.trim() !== (value.name || '')) onChange(null);
          } else if (reason === 'reset') {
            setInputValue(next);
          }
        }}
        onChange={(_, next) => {
          onChange(next || null);
          setInputValue(next?.name || '');
        }}
        getOptionLabel={(item) => item?.name || ''}
        isOptionEqualToValue={(a, b) => a?.id === b?.id}
        filterOptions={(x) => x}
        noOptionsText={noOptionsText}
        renderOption={(props, item) => {
          const sub = optionSubline(item);
          return (
            <li {...props} key={item.id}>
              <span>
                {item.name}
                {sub ? <span style={{ opacity: 0.7 }}>{` · ${sub}`}</span> : null}
              </span>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            sx={inputSx}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </IpdFormField>
  );
}
